// Estymator „zwykłych cen” instalacji (P4, kontrakt od koordynatora 2026-10-24):
//   GET /api/v1/kits/zwykle-ceny?kwp=&zuzycie_kwh=&magazyn_kwh=&kto_kupuje=&dach=&fazy=&zestaw_id=&wojewodztwo=
//   -> { od, do, zestaw_cena, montaz_od, montaz_do, zrodlo, data_cen }
// Przy `kto_kupuje=klient` `od`/`do` to sam montaż, przy `wykonawca` całe zlecenie. `kwp` może zostać pominięte,
// gdy jest `zuzycie_kwh`; `magazyn_kwh=0` znaczy bez magazynu; `fazy` domyślnie 3.
//
// Backend powstaje równolegle, więc każda awaria (404, 5xx, sieć, zły kształt) kończy się `null`, a ekran po
// prostu nie pokazuje widełek. Odpowiedzi trzymamy w pamięci po adresie (razem z zapytaniem w toku), więc
// powtórne zapytanie o te same parametry, np. po przełączeniu grupy wyników, nie idzie drugi raz do serwera.
// Do pól wpisywanych na bieżąco służy `useZwykleCeny` (opóźnienie i anulowanie poprzedniego zapytania).
import { apiFetch } from './apiFetch'

const BASE_URL = process.env.REACT_APP_KITS_API_URL || '/api/v1/kits'

const PARAMETRY = ['kwp', 'zuzycie_kwh', 'magazyn_kwh', 'kto_kupuje', 'dach', 'fazy', 'zestaw_id', 'wojewodztwo']
const KTO_KUPUJE = ['klient', 'wykonawca']
const DACHY = ['Dachówka', 'Blacha', 'Papa', 'Grunt', 'Inny']

/**
 * Zapytanie z parametrów: tylko znane pola i dozwolone wartości, w stałej kolejności (klucz pamięci). Null, gdy nie
 * ma ani mocy, ani zużycia, bo wtedy estymator nie ma z czego liczyć.
 */
export const zapytanieZwyklychCen = (p = {}) => {
  const q = new URLSearchParams()
  const liczba = (v) => (v === null || v === undefined || v === '' ? null : Number(v))
  PARAMETRY.forEach((k) => {
    const v = p[k]
    if (v === null || v === undefined || v === '') return
    if (['kwp', 'zuzycie_kwh', 'magazyn_kwh'].includes(k)) {
      const n = liczba(v)
      if (Number.isFinite(n) && n >= 0) q.set(k, String(Math.round(n * 100) / 100))
    } else if (k === 'kto_kupuje') {
      if (KTO_KUPUJE.includes(v)) q.set(k, v)
    } else if (k === 'dach') {
      if (DACHY.includes(v)) q.set(k, v)
    } else if (k === 'fazy') {
      if (String(v) === '1' || String(v) === '3') q.set(k, String(v))
    } else {
      q.set(k, String(v))
    }
  })
  const kwp = Number(q.get('kwp'))
  const zuzycie = Number(q.get('zuzycie_kwh'))
  if (!(kwp > 0) && !(zuzycie > 0)) return null
  return q.toString()
}

const opisZrodla = (z) => {
  if (typeof z === 'string') return z.trim() || null
  if (z && typeof z === 'object' && typeof z.opis === 'string') return z.opis.trim() || null
  return null
}

const liczbaLubNull = (v) => (Number.isFinite(Number(v)) && v !== null && v !== '' ? Number(v) : null)

/** Odpowiedź w ustalonym kształcie albo null, gdy nie ma widełek. */
export const normalizujZwykleCeny = (d) => {
  if (!d || typeof d !== 'object') return null
  const od = liczbaLubNull(d.od)
  const doKwoty = liczbaLubNull(d.do)
  if (!(od > 0) || !(doKwoty >= od)) return null
  return {
    od,
    do: doKwoty,
    zestaw_cena: liczbaLubNull(d.zestaw_cena),
    montaz_od: liczbaLubNull(d.montaz_od),
    montaz_do: liczbaLubNull(d.montaz_do),
    // Backend (1585ba5) zwraca `zrodlo` jako obiekt z opisem, datą, liczbą ofert i sklepami; dawniej napis.
    zrodlo: opisZrodla(d.zrodlo),
    zrodlo_szczegoly: d.zrodlo && typeof d.zrodlo === 'object' ? d.zrodlo : null,
    data_cen: typeof d.data_cen === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d.data_cen) ? d.data_cen.slice(0, 10) : null,
  }
}

const POJEMNOSC_PAMIECI = 50
const pamiec = new Map()

/** Czyści pamięć odpowiedzi (testy). */
export const wyczyscPamiecZwyklychCen = () => pamiec.clear()

/**
 * Zwykłe ceny dla parametrów albo null (brak danych, błąd, brak endpointu). Nigdy nie rzuca. `signal` przerywa
 * czekanie (zapytanie zostaje w pamięci i może się przydać następnemu wywołaniu z tymi samymi parametrami).
 */
export const pobierzZwykleCeny = async (parametry, { signal } = {}) => {
  const q = zapytanieZwyklychCen(parametry)
  if (!q) return null
  let obietnica = pamiec.get(q)
  if (!obietnica) {
    obietnica = Promise.resolve()
      .then(() => apiFetch(`${BASE_URL}/zwykle-ceny?${q}`))
      .then((res) => (res && res.ok ? res.json().then(normalizujZwykleCeny) : null))
      .catch(() => null)
    pamiec.set(q, obietnica)
    if (pamiec.size > POJEMNOSC_PAMIECI) pamiec.delete(pamiec.keys().next().value)
    // Brak odpowiedzi nie zostaje w pamięci: backend mógł właśnie dostać endpoint.
    obietnica.then((wynik) => { if (wynik === null && pamiec.get(q) === obietnica) pamiec.delete(q) })
  }
  if (!signal) return obietnica
  if (signal.aborted) return null
  return new Promise((resolve) => {
    const przerwij = () => resolve(null)
    signal.addEventListener('abort', przerwij, { once: true })
    obietnica.then((w) => { signal.removeEventListener('abort', przerwij); resolve(signal.aborted ? null : w) })
  })
}

/**
 * To samo pod nazwą z bazy (`feat/rynek-dla-domu`, formularz zlecenia P3: `zwykleCeny(parametry)` → odpowiedź
 * albo null). Jeden klient dla obu miejsc: pamięć i walidacja wspólne.
 */
export const zwykleCeny = (parametry) => pobierzZwykleCeny(parametry)
